/* ------------------------------------------------------------------
 * qv-analytics.js
 *
 * Instruments the Stiletto quick-view modal with dual-pipe analytics
 * (window.dataLayer + Shopify.analytics.publish), matching the
 * pattern from mobile-nav.liquid + search-modal.liquid +
 * home-analytics.js.
 *
 * Events emitted:
 *   - quick_view_open    { product_handle, product_id, source }
 *   - quick_view_close   { product_handle, dwell_ms, did_add_to_cart,
 *                          close_source }
 *   - quick_view_add_to_cart { product_handle, product_id, source,
 *                          ms_since_open }
 *
 * Source detection (where the modal was opened from): we sniff the
 * click ancestry of the trigger element on every quick-view click
 * BEFORE Stiletto's bubble-phase handler emits `quick-view:open`.
 *
 * State tracking: the modal element is GLOBAL — one [data-quick-view-modal]
 * lives at the bottom of layout/theme.liquid, used for every product.
 * We watch a class mutation on that node (`is-visible` added on open,
 * removed on close) to know when it's actually shown / dismissed.
 *
 * Bonus: this script also augments Elevar's dl_add_to_cart push so
 * downstream consumers can distinguish QV-originated ATC from PDP /
 * cart-drawer ATC. We wrap dataLayer.push and, while the QV modal is
 * open (we know because we just bound openedAt), we stamp
 * `source: "quick_view"` onto any dl_add_to_cart event. We do NOT
 * remove or rename any existing field — Elevar consumers continue to
 * see everything they already see.
 * ------------------------------------------------------------------ */
(function () {
  if (window.__rastahQvAnalyticsInit) return;
  window.__rastahQvAnalyticsInit = true;

  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (_) { /* never let tracking break navigation */ }
  }

  /* ---- Source inference -------------------------------------------- *
   * Walks up from the click target to figure out where the QV was
   * triggered from. Returns a snake_case string the parent agent can
   * pivot on. Order matters: more-specific contexts first.
   */
  function inferSource(triggerEl) {
    if (!triggerEl) return 'unknown';
    // Search modal (rastah-built modal, not Stiletto's)
    if (triggerEl.closest('.rastah-search, [data-rastah-search-modal], #rastah-search-modal')) {
      return 'search_modal';
    }
    // Search results page (sections/main-search.liquid)
    if (triggerEl.closest('[data-section-type="search"], .template-search [data-section-type="search-template"]')) {
      return 'search_results';
    }
    // Cart drawer cross-sells
    if (triggerEl.closest('.quick-cart')) {
      return 'cart_drawer_cross_sell';
    }
    // Cart recommendations / cross-sells block on cart page
    if (triggerEl.closest('.cross-sells, [data-section-type="cart-recommendations"]')) {
      return 'cross_sells';
    }
    // Shop the Look / shoppable
    if (triggerEl.closest('.shop-the-look, [data-shop-the-look], .shoppable-item, [data-shoppable]')) {
      return 'shop_the_look';
    }
    // Featured products / cross product blocks
    if (triggerEl.closest('[data-section-type^="featured-product"], [data-section-type="product-recommendations"], [data-section-type="recommended-products"]')) {
      return 'product_recommendations';
    }
    // Collection-tabs / collection grid (template-collection)
    var collectionLike = triggerEl.closest('[data-section-type="collection"], [data-section-type="collection-template"], [data-section-type^="featured-collection"]');
    if (collectionLike) {
      // Home page hosts featured-collection-* sections; the body
      // class distinguishes home vs. PLP.
      if (document.body && document.body.classList && document.body.classList.contains('template-index')) {
        return 'home_grid';
      }
      return 'collection_card';
    }
    // Home page generic grid
    if (document.body && document.body.classList && document.body.classList.contains('template-index')) {
      return 'home_grid';
    }
    // PLP fallback
    if (document.body && document.body.classList && document.body.classList.contains('template-collection')) {
      return 'collection_card';
    }
    return 'unknown';
  }

  /* ---- Handle extraction from product URL --------------------------- *
   * QV triggers carry the product URL on their href. The handle is the
   * last non-empty path segment after stripping `?view=...` query.
   */
  function handleFromUrl(url) {
    if (!url) return '';
    try {
      // Strip query/hash; URL ctor needs a base for relative paths.
      var u = new URL(url, window.location.origin);
      var parts = u.pathname.split('/').filter(Boolean);
      var i = parts.indexOf('products');
      if (i >= 0 && parts[i + 1]) return parts[i + 1];
      return parts[parts.length - 1] || '';
    } catch (_) {
      // Pure regex fallback.
      var m = String(url).match(/\/products\/([^/?#]+)/);
      return m ? m[1] : '';
    }
  }

  /* ---- State -------------------------------------------------------- */
  var modal = null;
  var openedAt = 0;
  var currentHandle = '';
  var currentProductId = '';
  var currentSource = '';
  var didAddToCart = false;
  var pendingCloseSource = '';

  /* ---- ATC source augmentation -------------------------------------- *
   * We wrap dataLayer.push so Elevar's dl_add_to_cart push gets a
   * `source: "quick_view"` stamp when fired while the QV modal is open.
   * Elevar pushes `dl_add_to_cart` from outside the theme; the theme
   * doesn't control its payload directly. The wrapper is transparent
   * — it returns whatever the original push returned and never mutates
   * any pre-existing field.
   */
  (function wrapDataLayer() {
    window.dataLayer = window.dataLayer || [];
    var origPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (entry) {
      try {
        if (entry && typeof entry === 'object' && entry.event === 'dl_add_to_cart') {
          // Only stamp source when the QV modal is currently open
          // AND no source has been provided already. Preserve any
          // existing `source` so we don't overwrite future
          // PDP/cart-drawer instrumentation Elevar might add.
          if (openedAt > 0 && entry.source == null) {
            entry.source = 'quick_view';
          }
        }
      } catch (_) { /* never break the dataLayer */ }
      return origPush(entry);
    };
  })();

  /* ---- Trigger capture --------------------------------------------- *
   * Bind document-level capture-phase click listeners on both
   * `.show-product-quickview` and `[data-quick-view-trigger]`. We
   * resolve source + handle BEFORE Stiletto's pubsub emit, then store
   * in module state for the subsequent open detection.
   *
   * Note: we don't preventDefault — Stiletto's own listener still
   * fires bubble-phase. We only observe.
   */
  document.addEventListener('click', function (e) {
    var trigger = e.target && e.target.closest
      ? e.target.closest('.show-product-quickview, [data-quick-view-trigger]')
      : null;
    if (!trigger) return;

    // For `<a>` triggers Stiletto reads href; for `[data-quick-view-trigger]`
    // it may live on a button with the URL on data-* or href. Try both.
    var url = trigger.getAttribute('href')
      || trigger.getAttribute('data-product-url')
      || trigger.getAttribute('data-url')
      || '';
    currentHandle = handleFromUrl(url);

    // Try to pull product_id from a parent card if surfaced. Many
    // product-item wrappers carry `data-product-id` or similar.
    var card = trigger.closest('[data-product-id]');
    currentProductId = card ? (card.getAttribute('data-product-id') || '') : '';

    currentSource = inferSource(trigger);
  }, true);

  /* ---- Modal lifecycle observation --------------------------------- *
   * Stiletto's _open/_close toggle `is-visible` on [data-quick-view-modal].
   * MutationObserver on class lets us catch every state change without
   * patching theme.js.
   */
  function bindModal() {
    modal = document.querySelector('[data-quick-view-modal]');
    if (!modal) return false;

    var wasVisible = modal.classList.contains('is-visible');

    var obs = new MutationObserver(function () {
      var nowVisible = modal.classList.contains('is-visible');
      if (nowVisible === wasVisible) return;
      wasVisible = nowVisible;
      if (nowVisible) {
        openedAt = Date.now();
        didAddToCart = false;
        pendingCloseSource = '';
        track('quick_view_open', {
          product_handle: currentHandle || '',
          product_id: currentProductId || '',
          source: currentSource || 'unknown'
        });
      } else {
        if (openedAt) {
          // If the ATC succeeded inside the modal, the auto-close
          // is a consequence of the ATC flow — not a user-initiated
          // dismissal. Stamp `atc_success` regardless of which
          // close path (X / scrim / programmatic) was triggered as
          // part of the post-ATC cleanup.
          var resolvedCloseSource = didAddToCart ? 'atc_success' : (pendingCloseSource || 'unknown');
          track('quick_view_close', {
            product_handle: currentHandle || '',
            dwell_ms: Date.now() - openedAt,
            did_add_to_cart: didAddToCart,
            close_source: resolvedCloseSource
          });
        }
        openedAt = 0;
        pendingCloseSource = '';
      }
    });
    obs.observe(modal, { attributes: true, attributeFilter: ['class'] });
    return true;
  }

  /* ---- Close-source hints ------------------------------------------ *
   * Pre-stamp pendingCloseSource on the events that lead to a close,
   * BEFORE Stiletto removes the `is-visible` class.
   */
  document.addEventListener('keydown', function (e) {
    if (!modal || !modal.classList.contains('is-visible')) return;
    if (e.key === 'Escape' || e.keyCode === 27) {
      pendingCloseSource = 'esc';
    }
  }, true);

  document.addEventListener('click', function (e) {
    if (!modal) return;
    if (!modal.classList.contains('is-visible')) return;
    var t = e.target;
    if (!t || !t.closest) return;
    // Scrim (wash) click
    if (t.closest('.modal__wash') && modal.contains(t)) {
      pendingCloseSource = 'scrim';
      return;
    }
    // X button
    if (t.closest('[data-modal-close]') && modal.contains(t)) {
      pendingCloseSource = 'x_button';
      return;
    }
  }, true);

  /* ---- ATC detection (in-QV) --------------------------------------- *
   * The QV form submit handler (assets/quick-view-form-guard.js)
   * dispatches a document-level `cart:item-added` after a successful
   * add. If openedAt is set when that fires, the ATC originated inside
   * the modal. We also flag the close-source as `atc_success` for the
   * subsequent close event.
   *
   * Stiletto's own QV flow also dispatches `cart:item-added` via
   * dispatchCustomEvent on success (theme.js ~6600) so this catches
   * both the fast-path and the form-guard fallback.
   */
  // Both paths can fire for one add, so quick_view_add_to_cart is
  // deduped to one event per 2 s. It is its own event (not just the
  // did_add_to_cart flag on close) so GA4 can count it per page
  // without a custom dimension: quick_view_ is already routed by GTM.
  var lastAddAt = 0;
  document.addEventListener('cart:item-added', function () {
    if (!openedAt) return;
    didAddToCart = true;
    pendingCloseSource = 'atc_success';
    var now = Date.now();
    if (now - lastAddAt < 2000) return;
    lastAddAt = now;
    track('quick_view_add_to_cart', {
      product_handle: currentHandle || '',
      product_id: currentProductId || '',
      source: currentSource || 'unknown',
      ms_since_open: now - openedAt
    });
  });

  /* ---- Boot --------------------------------------------------------- */
  function boot() {
    if (!bindModal()) {
      // Modal injected later (rare — it's rendered from layout/theme.liquid
      // so usually present at parse). Retry once on window.load.
      window.addEventListener('load', bindModal, { once: true });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
